import React, { useEffect, useMemo, useState } from 'react';
import { CardContainer, UsersTable, UsersTableBody, UsersTableBodyColumn, UsersTableHead, UsersTableHeadColumn, UsersTableRow } from './style';
import Card from '../../../Components/Miscellaneous/Card';
import axios from 'axios';
import AuthService from '../../../AuthService';
import Zoom from 'react-medium-image-zoom'
import Select from 'react-select';
import { BiPlus, BiPrinter, BiDownload, BiRefresh } from 'react-icons/bi';
import { PaginationButton, PaginationContainer } from '../../Categories/style';
import { Input, Loader } from '../../Login/style';
import Button from '../../../Components/Miscellaneous/Button';
import { toast } from 'react-toastify';
import CurrencyFormat from 'react-currency-format';

export default function Stocks(){

    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalProducts: 0,
        productsPerPage: 100
    });

    // Estados para filtros
    const [filters, setFilters] = useState({
        supplier: '',
        minStock: '',
        maxStock: '',
        category: '',
        reference: '',
        name: '',
        sortBy: 'stock',
        sortOrder: 'asc',
        limit: 100
    });

    const [availableSuppliers, setAvailableSuppliers] = useState([]);
    const [modalOpened, setModalOpened] = useState(false);
    const [navigateTo, setNavigateTo] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    // Función para construir query string con filtros
    const buildQueryString = (pageNumber = 1) => {
        const params = new URLSearchParams();
        params.append('page', pageNumber);
        params.append('limit', filters.limit);
        params.append('sortBy', filters.sortBy);
        params.append('sortOrder', filters.sortOrder);

        if (filters.supplier) params.append('supplier', filters.supplier);
        if (filters.minStock) params.append('minStock', filters.minStock);
        if (filters.maxStock) params.append('maxStock', filters.maxStock);
        if (filters.category) params.append('category', filters.category);
        if (filters.reference) params.append('reference', filters.reference);
        if (filters.name) params.append('name', filters.name);

        return params.toString();
    };

    // Función para obtener datos con filtros
    const fetchData = (pageNumber = 1) => {
        setLoading(true);
        const queryString = buildQueryString(pageNumber);

        axios.get(`https://api.rb.beeapp.es/admin/stocks?${queryString}`, {
            headers: {
                Authorization: 'Bearer ' + AuthService.getToken()
            }
        })
        .then(response => {
            setLoading(false);
            const data = response.data;

            setProducts(data.products || []);
            setPagination(data.pagination || {});
            setAvailableSuppliers(data.filters?.availableSuppliers || []);
        })
        .catch(error => {
            setLoading(false);
            console.error('Error fetching stocks:', error);
            toast.error('Error al cargar los productos');
        });
    };

    // Manejar cambio de filtros
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Aplicar filtros
    const applyFilters = () => {
        setSelectedProducts(new Set()); // Limpiar selección
        fetchData(1);
    };

    // Resetear filtros
    const resetFilters = () => {
        setFilters({
            supplier: '',
            minStock: '',
            maxStock: '',
            category: '',
            reference: '',
            name: '',
            sortBy: 'stock',
            sortOrder: 'asc',
            limit: 100
        });
        setSelectedProducts(new Set());
        setTimeout(() => fetchData(1), 100);
    };

    // Selección de productos
    const toggleSelectProduct = (productId) => {
        setSelectedProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    };

    const toggleSelectAll = () => {
        if (selectedProducts.size === products.length) {
            setSelectedProducts(new Set());
        } else {
            setSelectedProducts(new Set(products.map(p => p._id)));
        }
    };

    // Exportar a CSV
    const exportToCSV = () => {
        const productsToExport = products.filter(p => selectedProducts.has(p._id));

        if (productsToExport.length === 0) {
            toast.warning('Selecciona al menos un producto para exportar');
            return;
        }

        // Crear CSV
        const headers = ['Referencia', 'Nombre', 'Stock', 'Precio', 'Proveedor', 'Categoría'];
        const csvContent = [
            headers.join(','),
            ...productsToExport.map(p => [
                p.reference || '',
                `"${(p.name || '').replace(/"/g, '""')}"`,
                p.stock || 0,
                p.price || 0,
                `"${(p.supplier || '').replace(/"/g, '""')}"`,
                `"${(p.category?.[0] || '').replace(/"/g, '""')}"`
            ].join(','))
        ].join('\n');

        // Descargar archivo
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `stocks_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success(`${productsToExport.length} productos exportados a CSV`);
    };

    // Imprimir seleccionados
    const printSelected = () => {
        const productsToExport = products.filter(p => selectedProducts.has(p._id));

        if (productsToExport.length === 0) {
            toast.warning('Selecciona al menos un producto para imprimir');
            return;
        }

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Stock de Productos - ${new Date().toLocaleDateString()}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        h1 { text-align: center; color: #028322; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #028322; color: white; }
                        tr:nth-child(even) { background-color: #f2f2f2; }
                        .low-stock { background-color: #ffe6e6; }
                        @media print {
                            button { display: none; }
                        }
                    </style>
                </head>
                <body>
                    <h1>Reporte de Stock</h1>
                    <p><strong>Fecha:</strong> ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
                    <p><strong>Total de productos:</strong> ${productsToExport.length}</p>
                    <table>
                        <thead>
                            <tr>
                                <th>Referencia</th>
                                <th>Nombre</th>
                                <th>Stock</th>
                                <th>Precio</th>
                                <th>Proveedor</th>
                                <th>Categoría</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${productsToExport.map(p => `
                                <tr class="${p.stock < 10 ? 'low-stock' : ''}">
                                    <td>${p.reference || '-'}</td>
                                    <td>${p.name || '-'}</td>
                                    <td>${p.stock || 0}</td>
                                    <td>${(p.price || 0).toFixed(2)}€</td>
                                    <td>${p.supplier || '-'}</td>
                                    <td>${p.category?.[0] || '-'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <script>
                        window.onload = () => window.print();
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    // Paginación
    const handlePageChange = (pageNumber) => {
        fetchData(pageNumber);
    };

    return (
        <>
        {loading && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 100,
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '5px',
                    width: 'auto',
                    textAlign: 'center',
                }}>
                    <Loader />
                </div>
            </div>
        )}

        <div style={{ padding: '20px' }}>
            {/* Panel de Filtros */}
            <div style={{
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginBottom: '20px'
            }}>
                <h2 style={{ marginBottom: '15px', color: '#028322' }}>Filtros</h2>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {/* Filtro de Proveedor */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Proveedor</label>
                        <Select
                            options={[
                                { value: '', label: 'Todos' },
                                ...availableSuppliers.map(s => ({ value: s, label: s }))
                            ]}
                            value={{ value: filters.supplier, label: filters.supplier || 'Todos' }}
                            onChange={(option) => handleFilterChange('supplier', option.value)}
                            placeholder="Seleccionar proveedor"
                        />
                    </div>

                    {/* Filtro Stock Mínimo */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Stock Mínimo</label>
                        <Input
                            type="number"
                            value={filters.minStock}
                            onChange={(e) => handleFilterChange('minStock', e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    {/* Filtro Stock Máximo */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Stock Máximo</label>
                        <Input
                            type="number"
                            value={filters.maxStock}
                            onChange={(e) => handleFilterChange('maxStock', e.target.value)}
                            placeholder="10"
                        />
                    </div>

                    {/* Filtro por Nombre */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Nombre</label>
                        <Input
                            type="text"
                            value={filters.name}
                            onChange={(e) => handleFilterChange('name', e.target.value)}
                            placeholder="Buscar por nombre"
                        />
                    </div>

                    {/* Filtro por Referencia */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Referencia</label>
                        <Input
                            type="text"
                            value={filters.reference}
                            onChange={(e) => handleFilterChange('reference', e.target.value)}
                            placeholder="Buscar por ref"
                        />
                    </div>

                    {/* Ordenar por */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Ordenar por</label>
                        <Select
                            options={[
                                { value: 'stock', label: 'Stock' },
                                { value: 'name', label: 'Nombre' },
                                { value: 'reference', label: 'Referencia' },
                                { value: 'price', label: 'Precio' }
                            ]}
                            value={{ value: filters.sortBy, label: filters.sortBy === 'stock' ? 'Stock' : filters.sortBy === 'name' ? 'Nombre' : filters.sortBy === 'reference' ? 'Referencia' : 'Precio' }}
                            onChange={(option) => handleFilterChange('sortBy', option.value)}
                        />
                    </div>

                    {/* Orden */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Orden</label>
                        <Select
                            options={[
                                { value: 'asc', label: 'Ascendente' },
                                { value: 'desc', label: 'Descendente' }
                            ]}
                            value={{ value: filters.sortOrder, label: filters.sortOrder === 'asc' ? 'Ascendente' : 'Descendente' }}
                            onChange={(option) => handleFilterChange('sortOrder', option.value)}
                        />
                    </div>

                    {/* Límite por página */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Productos por página</label>
                        <Select
                            options={[
                                { value: 50, label: '50' },
                                { value: 100, label: '100' },
                                { value: 200, label: '200' },
                                { value: 500, label: '500' }
                            ]}
                            value={{ value: filters.limit, label: String(filters.limit) }}
                            onChange={(option) => handleFilterChange('limit', option.value)}
                        />
                    </div>
                </div>

                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                    <Button onClick={applyFilters} style={{ backgroundColor: '#028322' }}>
                        Aplicar Filtros
                    </Button>
                    <Button onClick={resetFilters} style={{ backgroundColor: '#6c757d' }}>
                        <BiRefresh style={{ marginRight: '5px' }} />
                        Resetear
                    </Button>
                </div>
            </div>

            {/* Panel de Acciones */}
            <div style={{
                backgroundColor: '#fff',
                padding: '15px',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px'
            }}>
                <div>
                    <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
                        {selectedProducts.size} de {products.length} seleccionados
                    </span>
                    <span style={{ color: '#6c757d' }}>
                        (Total: {pagination.totalProducts} productos)
                    </span>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                        onClick={exportToCSV}
                        disabled={selectedProducts.size === 0}
                        style={{ backgroundColor: '#17a2b8' }}
                    >
                        <BiDownload style={{ marginRight: '5px' }} />
                        Exportar CSV
                    </Button>
                    <Button
                        onClick={printSelected}
                        disabled={selectedProducts.size === 0}
                        style={{ backgroundColor: '#6f42c1' }}
                    >
                        <BiPrinter style={{ marginRight: '5px' }} />
                        Imprimir
                    </Button>
                </div>
            </div>

            {/* Tabla de Productos */}
            <UsersTable>
                <UsersTableHead>
                    <UsersTableHeadColumn style={{ width: '50px' }}>
                        <input
                            type="checkbox"
                            checked={selectedProducts.size === products.length && products.length > 0}
                            onChange={toggleSelectAll}
                            style={{ cursor: 'pointer' }}
                        />
                    </UsersTableHeadColumn>
                    <UsersTableHeadColumn>Imagen</UsersTableHeadColumn>
                    <UsersTableHeadColumn>Referencia</UsersTableHeadColumn>
                    <UsersTableHeadColumn>Producto</UsersTableHeadColumn>
                    <UsersTableHeadColumn>Proveedor</UsersTableHeadColumn>
                    <UsersTableHeadColumn>Stock</UsersTableHeadColumn>
                    <UsersTableHeadColumn>Precio</UsersTableHeadColumn>
                    <UsersTableHeadColumn>Acciones</UsersTableHeadColumn>
                </UsersTableHead>
                <UsersTableBody>
                    {products.length !== 0 ? products.map((product, index) => (
                        <UsersTableRow
                            key={product._id}
                            style={{
                                backgroundColor: product.stock < 5 ? '#ffe6e6' :
                                                product.stock < 10 ? '#fff3cd' : 'transparent'
                            }}
                        >
                            <UsersTableBodyColumn>
                                <input
                                    type="checkbox"
                                    checked={selectedProducts.has(product._id)}
                                    onChange={() => toggleSelectProduct(product._id)}
                                    style={{ cursor: 'pointer' }}
                                />
                            </UsersTableBodyColumn>
                            <UsersTableBodyColumn>
                                <Zoom>
                                    <img
                                        src={product.images?.[0] || 'https://via.placeholder.com/50'}
                                        alt={product.name}
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            objectFit: 'contain',
                                            borderRadius: '5px',
                                        }}
                                        loading='lazy'
                                    />
                                </Zoom>
                            </UsersTableBodyColumn>
                            <UsersTableBodyColumn>{product.reference || '-'}</UsersTableBodyColumn>
                            <UsersTableBodyColumn
                                onClick={() => window.location.href = '/admin/product-edit/' + product._id}
                                style={{ cursor: 'pointer', color: '#028322' }}
                            >
                                {product.name}
                            </UsersTableBodyColumn>
                            <UsersTableBodyColumn>{product.supplier || '-'}</UsersTableBodyColumn>
                            <UsersTableBodyColumn>
                                <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: product.stock < 5 ? '#dc3545' :
                                                    product.stock < 10 ? '#ffc107' : '#28a745',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}>
                                    {product.stock}
                                </span>
                            </UsersTableBodyColumn>
                            <UsersTableBodyColumn>
                                <CurrencyFormat
                                    value={product.price}
                                    displayType={'text'}
                                    thousandSeparator={false}
                                    suffix={'€'}
                                    decimalSeparator=','
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                />
                            </UsersTableBodyColumn>
                            <UsersTableBodyColumn>
                                <button
                                    onClick={() => window.location.href = '/admin/product-edit/' + product._id}
                                    style={{
                                        padding: '5px 10px',
                                        backgroundColor: '#028322',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Editar
                                </button>
                            </UsersTableBodyColumn>
                        </UsersTableRow>
                    )) : (
                        <UsersTableRow>
                            <UsersTableBodyColumn colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                                No se encontraron productos
                            </UsersTableBodyColumn>
                        </UsersTableRow>
                    )}
                </UsersTableBody>
            </UsersTable>

            {/* Paginación */}
            {pagination.totalPages > 1 && (
                <PaginationContainer style={{ marginTop: '20px' }}>
                    <PaginationButton
                        disabled={pagination.currentPage === 1}
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                    >
                        Anterior
                    </PaginationButton>

                    <span style={{ margin: '0 15px' }}>
                        Página {pagination.currentPage} de {pagination.totalPages}
                    </span>

                    <PaginationButton
                        disabled={pagination.currentPage === pagination.totalPages}
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                    >
                        Siguiente
                    </PaginationButton>

                    <button
                        onClick={() => setModalOpened(true)}
                        style={{
                            marginLeft: '10px',
                            padding: '8px 16px',
                            backgroundColor: '#028322',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                    >
                        Ir a página...
                    </button>
                </PaginationContainer>
            )}

            {/* Modal ir a página */}
            {modalOpened && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '5px',
                        width: '300px',
                    }}>
                        <span
                            style={{
                                height: '20px',
                                width: '20px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '1rem',
                                position: 'relative',
                                top: '-10px',
                                right: '-270px',
                                cursor: 'pointer',
                                boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
                                padding: '5px',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                            }}
                            onClick={() => setModalOpened(false)}
                        >
                            &times;
                        </span>
                        <h3>Ir a página</h3>
                        <Input
                            type="number"
                            placeholder={`Página (1-${pagination.totalPages})`}
                            style={{ width: '100%' }}
                            onChange={(e) => setNavigateTo(e.target.value)}
                        />
                        <Button
                            width="100%"
                            style={{ marginTop: '10px' }}
                            onClick={() => {
                                const page = parseInt(navigateTo);
                                if (page > 0 && page <= pagination.totalPages) {
                                    handlePageChange(page);
                                    setModalOpened(false);
                                } else {
                                    toast.error('Número de página inválido');
                                }
                            }}
                        >
                            Ir a la página
                        </Button>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}
